import { useMemo } from 'react';

/** 21kg CO2 = 1 tree (annual absorption equivalent) */
export const CO2_PER_TREE_KG = 21;

export type TreeStage = 'sapling' | 'young' | 'mature' | 'lush';

interface TreeGrowthConfig {
  /** Number of trees equivalent saved */
  treesSaved: number;
  /** Current growth stage */
  stage: TreeStage;
  /** Number of leaves to render */
  leafCount: number;
  /** Trunk width multiplier (1 = base) */
  trunkScale: number;
  /** Canopy radius multiplier */
  canopyScale: number;
}

/** Visual configuration per growth stage */
interface StageConfig {
  leafCount: number;
  trunkScale: number;
  canopyScale: number;
}

/** Centralized growth configuration by stage */
const GROWTH_BY_STAGE: Record<TreeStage, StageConfig> = {
  sapling: { leafCount: 6, trunkScale: 0.5, canopyScale: 0.4 },
  young: { leafCount: 18, trunkScale: 0.7, canopyScale: 0.65 },
  mature: { leafCount: 35, trunkScale: 0.85, canopyScale: 0.85 },
  lush: { leafCount: 55, trunkScale: 1.0, canopyScale: 1.0 },
};

/** CO2 thresholds (metric tons) for stage transitions */
const STAGE_THRESHOLDS = {
  young: 0.5,
  mature: 2,
  lush: 5,
} as const;

/**
 * Hook to calculate tree growth state based on CO2 reduced
 *
 * Tree evolution states:
 * - sapling: < 0.5t CO2 (5-8 leaves, thin stem)
 * - young: 0.5-2t CO2 (15-20 leaves, small trunk)
 * - mature: 2-5t CO2 (30-40 leaves, medium trunk)
 * - lush: 5t+ CO2 (50-60 leaves, full canopy)
 */
export function useTreeGrowth(co2MetricTons: number): TreeGrowthConfig {
  const treesSaved = useMemo(() => {
    return Math.round((co2MetricTons * 1000) / CO2_PER_TREE_KG);
  }, [co2MetricTons]);

  const stage = useMemo((): TreeStage => {
    if (co2MetricTons >= STAGE_THRESHOLDS.lush) return 'lush';
    if (co2MetricTons >= STAGE_THRESHOLDS.mature) return 'mature';
    if (co2MetricTons >= STAGE_THRESHOLDS.young) return 'young';
    return 'sapling';
  }, [co2MetricTons]);

  const config = GROWTH_BY_STAGE[stage];

  return {
    treesSaved,
    stage,
    leafCount: config.leafCount,
    trunkScale: config.trunkScale,
    canopyScale: config.canopyScale,
  };
}

/** Tree color palette - Hollywood green theme */
export const TREE_COLORS = {
  trunk: '#5D4E37',           // Warm brown
  trunkShadow: '#4A3F2E',     // Darker brown
  leaf: '#8FB560',            // Hollywood green (brand)
  leafShadow: '#7A9B4F',      // Darker green for depth
  leafHighlight: '#A5C878',   // Lighter green for highlights
};
